import React from 'react';

import mainStyle from "../../dashboard.module.scss";
import { useTheme, styled } from '@mui/material/styles';
import { Box, List, ListItem, Typography, Tooltip } from '@mui/material';

import { ReactComponent as SquareGreenIcon } from 'assets/images/icons/icn_chart_square_green.svg';

import { bytesToGB, bytesToMB } from "utils/format";

const TooltipBox = styled(Box)(({ theme }) => ({
  fontSize: theme.typography.customFontSize[1],
  fontWeight: theme.typography.customFontWeight[3],
  lineHeight: 1.85,
  color: theme.palette.primary.white,
  whiteSpace: 'nowrap',
  margin: '0 10px',
}));

const UsageRankingList = ({ titleIcon, title, sortable, data, openDeviceDrawer }) => {
  const theme = useTheme();

  return (
    <div
      className={`main-card-content ${mainStyle["dndObj_card"]} ${sortable ? mainStyle['dndObj_edit'] : ''}`}
      style={{ position: 'relative' }}
    >
      <div className={mainStyle['dndObj_title']}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 24, height: 24 }}>
          <img src={titleIcon} alt={title} />
        </Box>
        <span style={{ marginLeft: '10px' }}>{title}</span>
      </div>
      <List sx={{ paddingTop: 0, paddingBottom: 0, height: 168 }}>
        {data.length > 0 ?
          data.map((item, index) => (
            <ListItem
              key={`${title}_${item.name || item.deviceId}_${index}`}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingY: 0,
                marginY: '8px',
                height: '24px',
                overflow: 'hidden',
                "&:hover": {
                  backgroundColor: theme.palette.primary.lightBlue,
                  cursor: 'pointer',
                  borderRadius: '10px',
                }
              }}
              onClick={() => {
                if (title === 'Top 5 SSIDs by Traffic') return
                if (title === 'Top 5 Wireless Clients by Traffic') return openDeviceDrawer("clients", true, item.clientId)
                openDeviceDrawer("devices", true, item.deviceId)
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 0,
                  flex: 1,
                  overflow: 'hidden',
                }}
              >
                <SquareGreenIcon
                  style={{
                    marginRight: 8,
                    flexShrink: 0,
                  }}
                />
                {
                  title !== 'Top 5 Access Points by Wireless Client' ?
                    <Tooltip
                      arrow
                      slotProps={{
                        tooltip: {
                          sx: {
                            maxWidth: 'none',
                          }
                        }
                      }}
                      title={
                        <Box>
                          {[
                            ["Total Traffic (24h)", item.value, "GB"],
                            ["Tx", item.totalTx, "GB"],
                            ["Rx", item.totalRx, "GB"],
                          ].map(([label, value, unit]) => (
                            <Box
                              key={`${title}_${label}`}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 0.5,
                              }}
                            >
                              <TooltipBox>{label}</TooltipBox>
                              <TooltipBox>
                                {bytesToGB(value)} {unit}
                              </TooltipBox>
                            </Box>
                          ))}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 0.5,
                            }}
                          >
                            <TooltipBox>Avg Throughput</TooltipBox>

                            <TooltipBox sx={{ display: "flex", gap: "4px" }}>
                              ≈ {bytesToMB(item.rateAvg)} Mbps (Peak: {bytesToMB(item.ratePeak)} Mbps)
                            </TooltipBox>
                          </Box>
                        </Box>
                      }
                    >
                      <Typography
                        sx={{
                          color: theme.palette.primary.greyPrimary,
                          fontSize: theme.typography.customFontSize[3],
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          '&:hover': {
                            color: theme.palette.primary.mainHover,
                            textDecorationColor: theme.palette.primary.mainHover,
                            textDecoration: 'underline',
                            fontWeight: theme.typography.customFontWeight[1]
                          },
                        }}
                      >
                        {item?.name || item?.deviceId || item?.clientId}
                      </Typography>
                    </Tooltip> :
                    <Typography
                      sx={{
                        color: theme.palette.primary.greyPrimary,
                        fontSize: theme.typography.customFontSize[3],
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          color: theme.palette.primary.mainHover,
                          textDecorationColor: theme.palette.primary.mainHover,
                          textDecoration: 'underline',
                          fontWeight: theme.typography.customFontWeight[1]
                        },
                      }}
                    >
                      {item?.name || item?.deviceId || item?.clientId}
                    </Typography>
                }
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  flexShrink: 0,
                  marginLeft: 1,
                }}
              >
                <Typography
                  sx={{
                    color: theme.palette.primary.greyPrimary,
                    fontWeight: theme.typography.customFontWeight[1],
                    fontSize: theme.typography.customFontSize[3],
                    lineHeight: 1,
                  }}
                >
                  {item.unit === "GB" ? bytesToGB(item.value) : item.value}
                </Typography>
                <Typography
                  sx={{
                    color: theme.palette.primary.greyThird,
                    fontSize: theme.typography.customFontSize[3],
                    marginLeft: '10px',
                    lineHeight: 1,
                  }}
                >
                  {item.unit}
                </Typography>
              </Box>
            </ListItem>
          )) :
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}>
            <Typography
              sx={{
                color: theme.palette.primary.greyThird,
                fontSize: theme.typography.customFontSize[2],
              }}>
              No data available
            </Typography>
          </Box>
        }
      </List>
    </div>
  );
};

export default UsageRankingList;